// error-id.ts — 19BL Session 2 · client-side error handle generation.
//
// The error id is the single thread that ties together three otherwise
// disconnected things:
//   1. what the USER sees on the fallback UI / toast — `错误号 err_abcd1234` —
//      so they can quote it to the operator without describing the bug,
//   2. the D1 client_events PRIMARY KEY (Session 1 made the server accept a
//      client-generated id with INSERT OR IGNORE idempotency),
//   3. what the AGENT looks up in admin (Session 5) to pull the full
//      ErrorDiagnosis packet (view / action / stack / HTTP).
//
// Shape contract (pinned by tests/lib/error-id.test.ts):
//   - prefix `err_` (ERROR_ID_PREFIX) — distinguishes from anon ids / run ids
//   - body: lowercase alphanumeric, 8-80 chars — URL-safe + valid as a D1 TEXT
//     primary key under TelemetryEventInputSchema.id (≤80)
//
// Generation mirrors auth/anon-id.ts generateId(): prefer crypto.randomUUID
// when available, fall back to a time+random mix for older runtimes / jsdom.
// We do NOT reuse anon-id's function directly because (a) it's private to that
// module and (b) error ids need the `err_` prefix + base36 body normalization.

export const ERROR_ID_PREFIX = 'err_';

/**
 * Generate a fresh, globally-unique error handle. Safe to call in any browser
 * context (including jsdom without crypto.randomUUID) — never throws.
 */
export function generateErrorId(): string {
  return ERROR_ID_PREFIX + randomBody();
}

function randomBody(): string {
  if (hasRandomUUID()) {
    // crypto.randomUUID() → "xxxxxxxx-xxxx-...". Strip hyphens, lowercase to
    // base36-safe alnum. Keeps 32 hex chars → still high-entropy + collision-
    // free at our scale (single-digit ingests/min per recorder.ts self-assess).
    return crypto.randomUUID().replace(/-/g, '').toLowerCase();
  }
  // Fallback (older Safari / jsdom / non-secure context): time + Math.random.
  // Two sources so a tight loop still varies even if Math.random collides.
  return (
    Date.now().toString(36) +
    Math.random().toString(36).slice(2, 10) +
    Math.random().toString(36).slice(2, 10)
  ).toLowerCase();
}

function hasRandomUUID(): boolean {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function';
}

/**
 * Render an error id as a user-facing reference string. The id appears VERBATIM
 * (it's the load-bearing part the operator greps); the surrounding label is
 * human gloss and may evolve. Returned string is what gets shown on fallback
 * UIs and toasts.
 *
 *   formatErrorRef('err_abcd1234') → '错误号 err_abcd1234'
 */
export function formatErrorRef(id: string): string {
  return `错误号 ${id}`;
}
